/**
 * Geometry-free core of the Delete sub-sub-tool's "prune a hanging end"
 * behaviour (issue #670).
 *
 * When the Delete tool hovers a construction line that crosses another line and
 * pokes out past the crossing, we want to highlight just that overhanging stub
 * — the stretch between an endpoint and the nearest crossing — so a tap trims
 * the line back to the junction instead of deleting the whole thing. Anywhere
 * else on the line keeps today's per-segment behaviour.
 *
 * The Paper.js math (finding where other lines cross, projecting the cursor
 * onto the path) lives in `ConstructionTool`; this module owns only the pure
 * decision: given the cursor's offset along an OPEN subpath and the offsets of
 * the crossings on it, is the cursor sitting on a hanging end, and if so where
 * does the prune cut?
 */

/** A hanging end the cursor is over, expressed as offsets along the subpath. */
export interface HangingEnd {
  /** Which extremity hangs: the path start (offset 0) or end (offset length). */
  end: "start" | "end";
  /** Offset of the crossing the line is pruned back to. */
  cutOffset: number;
  /** Start offset of the overhanging portion (for the highlight overlay). */
  from: number;
  /** End offset of the overhanging portion (for the highlight overlay). */
  to: number;
}

/**
 * Decide whether `cursorOffset` (distance along an open subpath of total
 * `length`) sits on a hanging end, given the offsets where *other* lines cross
 * this subpath.
 *
 * Returns `null` when there are no usable crossings, or when the cursor lies in
 * an interior stretch bounded by crossings on both sides — those keep today's
 * whole-segment highlight. Crossings within `epsilon` of either endpoint are
 * ignored: a line that merely touches an endpoint leaves nothing hanging past
 * it.
 */
export function findHangingEnd(
  cursorOffset: number,
  crossings: ReadonlyArray<number>,
  length: number,
  epsilon = 0.05,
): HangingEnd | null {
  if (!(length > 0)) return null;
  // Keep only crossings strictly inside the path — a crossing at (or within
  // epsilon of) an endpoint can't carve off a hanging stub.
  const xs = crossings
    .filter((o) => o > epsilon && o < length - epsilon)
    .sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const first = xs[0]!;
  const last = xs[xs.length - 1]!;
  if (cursorOffset < first) {
    // Cursor between the start endpoint and the first crossing → prune the
    // [0, first] stub, keeping the rest.
    return { end: "start", cutOffset: first, from: 0, to: first };
  }
  if (cursorOffset > last) {
    return { end: "end", cutOffset: last, from: last, to: length };
  }
  return null;
}
